import { useRef } from "react";
import styles from "./FileInput.module.scss";
import { Controller } from "react-hook-form";
import type { Control, RegisterOptions, UseFormRegister } from "react-hook-form";
import withLabelAndError from "../withLabelAndError";
import toast from "@/lib/toast";

export interface FileInputProps {
  control?: Control<any>;
  register?: UseFormRegister<any>;
  validations?: RegisterOptions;
  name: string;
  placeholder?: string;
  [key: string]: unknown;
}

const getFileExtension = (fileName: string): string => "." + (fileName.split(".").pop()?.toLowerCase() ?? "");

const matchesExtension = (fileExt: string, rule: string): boolean =>
  rule.startsWith(".") && fileExt === rule;

const matchesCategory = (mimeType: string, rule: string): boolean =>
  rule.endsWith("/*") && mimeType.startsWith(rule.split("/")[0] + "/");

const matchesExact = (mimeType: string, rule: string): boolean => mimeType === rule;

const isFileAccepted = (fileExt: string, mimeType: string, accept: string[] = []): boolean => {
  return accept.some(
    (rule) =>
      matchesExtension(fileExt, rule) || matchesCategory(mimeType, rule) || matchesExact(mimeType, rule),
  );
};

const FileInput = ({
  control,
  register,
  validations,
  name,
  placeholder = "Select files...",
  ...rest
}: FileInputProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const renderInput = (field: { value: File[]; onChange: (value: File[]) => void }) => {
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      const accept = e.target.accept?.split(",").map((a) => a.trim().toLowerCase());

      const validFiles: File[] = [];
      const invalidFiles: string[] = [];

      files.forEach((file) => {
        const fileExt = getFileExtension(file.name);
        const mimeType = file.type.toLowerCase();

        const isValid = isFileAccepted(fileExt, mimeType, accept);

        if (isValid) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        toast.error(`These files are not allowed: ${invalidFiles.join(", ")}`);
      }

      field.onChange(validFiles);
    };

    const handleRemoveFile = (index: number) => {
      const newFiles = field.value.filter((_, i) => i !== index);
      field.onChange(newFiles);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    };

    return (
      <div className={styles.fileWrapper}>
        <input
          {...rest}
          type="file"
          id={name}
          name={name}
          ref={inputRef}
          className={styles.hiddenFileInput}
          onChange={handleFileChange}
        />

        <div className={`${styles.inputField} ${styles.chipInput}`}>
          {field.value && field.value.length > 0 ? (
            field.value.map((file, index) => (
              <abbr title={file.name} key={file.name + index}>
                <span className={styles.fileChip}>
                  <p>{file.name}</p>
                  <button type="button" onClick={() => handleRemoveFile(index)} className={styles.removeChip}>
                    &times;
                  </button>
                </span>
              </abbr>
            ))
          ) : (
            <span className={styles.placeholder}>{placeholder}</span>
          )}
        </div>
        <label htmlFor={name} className={styles.fileButton}>
          Select Files
        </label>
      </div>
    );
  };

  if (!register) {
    return (
      control && (
        <Controller
          control={control}
          name={name}
          rules={validations ?? {}}
          render={({ field }) => renderInput(field as { value: File[]; onChange: (value: File[]) => void })}
        />
      )
    );
  } else {
    const field = register(name, {
      ...validations,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        validations?.onChange?.(e) ?? Array.from(e.target.files || []),
    });

    return renderInput(field as unknown as { value: File[]; onChange: (value: File[]) => void });
  }
};

const FileInputWithLabelAndError = withLabelAndError(FileInput);

export default FileInputWithLabelAndError;
export { FileInput };
